import {
  Button,
  Flex,
  FormControl,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  useToast,
} from '@chakra-ui/react';
import {
  shipmentsApi,
  ShipmentState,
  UpdateShipmentRequest,
} from '@texas/api/endpoints/shipmentsApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { request } from '@texas/utils/helpers/httpHelpers';
import { ErrorDetails } from '../alert/ErrorDetails';
import DatePicker from 'react-datepicker';
import { ErrorLabel } from '../ErrorLabel';
import { TexasFormLabel } from '../form/TexasFormLabel';
import { UserAvatar } from '../users/UserAvatar';
import { compareEnum } from '@texas/utils/helpers/enumHelpers';
import { SimpleNote } from '../SimpleNote';
import { formatDate } from '@texas/utils/helpers/dateHelper';
import { Locale } from '@texas/i18n/types';
import { TransportRequestsSummary } from './TransportRequestsSummary';
import { FormCounter } from '../form/FormCounter';

const maxLength = 60;

export function EditShipmentModal({
  shipmentId,
  onChange,
  isOpen,
  onClose,
}: {
  shipmentId: number;
  onChange: () => void;
  isOpen: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Modal onClose={onClose} isOpen={isOpen} closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader pb={0}>{t('shipment.editShipment')}</ModalHeader>
        <ModalCloseButton />
        <Inner
          shipmentId={shipmentId}
          onUpdate={() => {
            onChange();
            onClose();
          }}
          onClose={onClose}
        />
      </ModalContent>
    </Modal>
  );
}

function Inner({
  shipmentId,
  onUpdate,
  onClose,
}: {
  shipmentId: number;
  onUpdate: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const { data, refetch, loading, error } = useApiResource(
    shipmentsApi.getShipment,
  );

  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { isSubmitting, isValid, errors },
    reset,
  } = useForm<UpdateShipmentRequest>({
    defaultValues: {
      dispatchDate: new Date(),
      trackingNumber: '',
      note: '',
    },
  });

  useEffect(() => {
    refetch(shipmentId);
  }, [refetch, shipmentId]);

  useEffect(() => {
    if (!data) return;
    reset({
      dispatchDate: new Date(data.dispatchDate),
      trackingNumber: data.trackingNumber,
      note: data.note,
    });
  }, [data, reset]);

  const { request: updateRequest, error: updateError } = useApiRequest(
    shipmentsApi.updateShipment,
  );

  const onSubmit = async (data: UpdateShipmentRequest) =>
    await request(updateRequest, [shipmentId, data], () => {
      toast({
        title: t('general.updated'),
        status: 'success',
        isClosable: true,
      });
      onUpdate();
      onClose();
    });
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ModalBody>
        {loading && <Spinner />}
        {error && <ErrorDetails error={error} />}
        {updateError && <ErrorDetails error={updateError} />}
        {data && (
          <Flex gap={4} flexDir="column">
            {compareEnum(
              ShipmentState,
              data.state,
              ShipmentState.Dispatched,
            ) && <SimpleNote>{t('shipment.editDispatchedWarning')}</SimpleNote>}
            <TransportRequestsSummary
              ticketNumbers={data.ticketNumbers ?? []}
              samplesCount={data.samplesInShipment.length}
            />
            <FormControl>
              <TexasFormLabel>{t('shipment.whoWillRecieve')}</TexasFormLabel>
              <UserAvatar
                name={data.receiverUser.name}
                email={data.receiverUser.email}
                label={data.receiverUser.label}
                branch={data.receiverUser.branchIdentifier}
                showTeams={false}
              />
            </FormControl>
            <FormControl>
              <TexasFormLabel>{t('shipment.courier')}</TexasFormLabel>
              <Text>{data.courier}</Text>
            </FormControl>

            <FormControl isRequired={true}>
              <TexasFormLabel>{t('shipment.trackingNumber')}</TexasFormLabel>
              <Input
                {...register('trackingNumber', { maxLength: maxLength })}
                variant="texas"
                placeholder="Enter code for tracking"
              />
              <FormCounter
                length={watch('trackingNumber').length}
                maxLength={maxLength}
              />
              <ErrorLabel text={errors.trackingNumber?.message} />
            </FormControl>
            {compareEnum(
              ShipmentState,
              data.state,
              ShipmentState.Dispatched,
            ) ? (
              <Flex direction="column">
                <TexasFormLabel>{t('shipment.dispatchDate')}</TexasFormLabel>
                <Text>{formatDate(Locale.En, data.dispatchDate)}</Text>
              </Flex>
            ) : (
              <FormControl isRequired={true}>
                <TexasFormLabel>{t('shipment.dispatchDate')}</TexasFormLabel>
                <Controller
                  control={control}
                  name="dispatchDate"
                  rules={{ required: true }}
                  render={({ field }) => (
                    <DatePicker
                      selected={field.value}
                      onChange={(date) => field.onChange(date)}
                      inline={true}
                      calendarStartDay={1}
                    />
                  )}
                />
                <ErrorLabel text={errors.dispatchDate?.message} />
              </FormControl>
            )}
          </Flex>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          type="submit"
          isLoading={isSubmitting}
          isDisabled={isSubmitting || !isValid}
        >
          {t('general.update')}
        </Button>
      </ModalFooter>
    </form>
  );
}
